import { useAppDispatch } from '@/app/redux/hooks';
import { useStudents } from '@/app/redux/hooks/students';
import {
  setStudentCreationCallback,
  setStudentCreationModalOpen,
} from '@/app/redux/slices/modalsSlice';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { LearnerMultiSelect } from '../MultiSelect';

interface AddLearnersToGroupDialogProps {
  groupId: string;
  existingStudentIds: string[];
  open: boolean;
  onClose: () => void;
  onSubmit: (studentIds: string[]) => Promise<void>;
}

export default function AddLearnersToGroupDialog({
  groupId,
  existingStudentIds,
  open,
  onClose,
  onSubmit,
}: AddLearnersToGroupDialogProps) {
  const dispatch = useAppDispatch();
  const { students } = useStudents();

  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [temporarilyHidden, setTemporarilyHidden] = useState(false);

  const handleAddItem = () => {
    setTemporarilyHidden(true);
    setStudentCreationCallback((studentId) => {
      setTemporarilyHidden(false);
      if (studentId) {
        setSelectedStudents((prev) => new Set([...prev, studentId]));
      }
    });
    dispatch(setStudentCreationModalOpen(true));
  };

  useEffect(() => {
    setSelectedStudents(new Set());
  }, [groupId]);

  const handleFilter = (searchTerm: string) => {
    return (students || []).filter((student) => {
      const key = `${student.studentFirstName} ${student.studentSurname} ${student.spellcastUsername}`;
      const terms = searchTerm.split(' ');
      return terms.every((term) =>
        key.toLowerCase().includes(term.toLowerCase()),
      );
    });
  };

  const handleSelect = (studentId: string) => {
    setSelectedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await onSubmit(Array.from(selectedStudents));
      setSelectedStudents(new Set());
      onClose();
    } catch (error) {
      console.error('Error updating group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open && !temporarilyHidden} onOpenChange={onClose}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add learners to group</DialogTitle>
          <DialogDescription>
            Search and select learners to add to this group
          </DialogDescription>
        </DialogHeader>
        <LearnerMultiSelect
          items={students}
          selectedIds={Array.from(selectedStudents)}
          onSelect={handleSelect}
          onFilter={handleFilter}
          getItemDisabled={(s) => existingStudentIds.includes(s.id)}
          autoFocus
          onAddItem={handleAddItem}
        />
        <div className="flex justify-end gap-2 mt-4">
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={selectedStudents.size === 0 || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              'Add Learners'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
